import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { trackEvent } from "../utils/analytics";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — left empty by real users
  const [validEmail, setValidEmail] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [somethingWrong, setSomethingWrong] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact",
    "url": "https://www.thrombio.com.au/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Thrombio",
      "url": "https://www.thrombio.com.au/",
      "logo": "https://www.thrombio.com.au/thromBioInvert.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      },
      "sameAs": [
        "https://www.linkedin.com/company/thrombioholdings"
      ]
    }
  };

  function handleSubmit() {
    setSubmitted(true);
    setButtonLoading(true);
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setValidEmail(regex.test(email));
    if (name && regex.test(email) && message) {
      trackEvent('form_submit', { form_name: 'contact', form_status: 'attempt' });
      fetch("/api/contact", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ name, email, company, message, website })
      })
        .then((result) => {
          if (!result.ok) throw new Error(`Contact API responded ${result.status}`);
          trackEvent('form_submit', { form_name: 'contact', form_status: 'success' });
        })
        .catch(() => {
          trackEvent('form_submit', { form_name: 'contact', form_status: 'error' });
          setSomethingWrong(true);
        })
        .finally(() => {
          setSubmitted(true);
          setButtonLoading(false);
        });
    } else {
      trackEvent('form_submit', { form_name: 'contact', form_status: 'validation_fail' });
      setShowErrors(true);
      setSubmitted(false);
      setButtonLoading(false);
    }
  }

  useEffect(() => {
    if (showErrors) {
      setTimeout(() => {
        setShowErrors(false);
      }, 5000);
    }
  }, [showErrors]);

  return (
    <>
      <SEO
        title="Contact Us"
        description="If you are interested in learning more about our renowned research, please contact us!"
        keywords={['Contact', 'Inquiries', 'Biotech Contact', 'Research Collaboration', 'Drug Development Partnership']}
        ogImage="/contactbrain1.webp"
        schema={contactSchema}
      />

      <div className="flex flex-col h-max min-h-[80dvh]">
        {/* Updated alt tag */}
        <img src="/contactBrain.jpg" alt="Simple outlined structure of the human brain" className="absolute right-0 top-40 md:hidden" />
        <img src="/contactBrain.jpg" alt="Simple outlined structure of the human brain" className="absolute right-0 top-40 max-md:hidden xl:hidden" />
        <div className="page-width px-20 pt-[80px] relative xl:flex items-center w-full py-28">
          <div className="grow">
            {/* Optimized H1 tag with sr-only class to preserve layout */}
            <h1 className="sr-only">Contact ThromBio: Connect with Us for Stroke Treatment Innovations</h1>
            <div className="relative font-bold text-[4.8rem] leadingg-[2.6rem] tracking-[.144rem] text-[#C3EEFD]">
              Contact
            </div>
            <div className="flex flex-col gap-10 relative bg-transparent z-0">
              <div className="flex flex-col gap-2">
                <p className="text-[2.1rem] leading-[3.1rem]">Name</p>
                <input
                  onChange={(e) => setName(e.target.value)}
                  readOnly={submitted}
                  className={`bg-white text-black text-[1.5rem] border border-gray-300 px-6 py-4 rounded-[18px] min-h-[4.8rem] shadow-xs placeholder:text-gray-500 placeholder:opacity-50 ring ring-offset-2 transition duration-500 ${showErrors && !name ? "ring-primary-red" : "ring-transparent"} ${submitted ? "bg-gray-400/50" : ""}`}
                  type="text"
                  placeholder="Enter your name"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[2.1rem] leading-[3.1rem]">Email</p>
                <input
                  onChange={(e) => setEmail(e.target.value)}
                  readOnly={submitted}
                  className={`bg-white text-black text-[1.5rem] border border-gray-300 px-6 py-4 rounded-[18px] min-h-[4.8rem] shadow-xs placeholder:text-gray-500 placeholder:opacity-50 ring ring-offset-2 transition duration-500 ${showErrors && !validEmail ? "ring-primary-red" : "ring-transparent"} ${submitted ? "bg-gray-400/50" : ""}`}
                  type="email"
                  placeholder="Enter your email"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[2.1rem] leading-[3.1rem]">Company (optional)</p>
                <input
                  onChange={(e) => setCompany(e.target.value)}
                  readOnly={submitted}
                  className={`bg-white text-black text-[1.5rem] border border-gray-300 px-6 py-4 rounded-[18px] min-h-[4.8rem] shadow-xs placeholder:text-gray-500 placeholder:opacity-50 ${submitted ? "bg-gray-400/50" : "bg-[#72909A]"}`}
                  type="text"
                  placeholder="Enter your company"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-[2.1rem] leading-[3.1rem]">Comment</p>
                <textarea
                  onChange={(e) => setMessage(e.target.value)}
                  readOnly={submitted}
                  className={`resize-none bg-white text-black text-[1.5rem] border border-gray-300 px-6 py-4 rounded-[18px] min-h-[4.8rem] shadow-xs placeholder:text-gray-500 placeholder:opacity-50 ring ring-offset-2 transition duration-500 ${showErrors && !message ? "ring-primary-red" : "ring-transparent"} ${submitted ? "bg-gray-400/50" : ""}`}
                  placeholder="Enter your message"
                  rows={6}
                />
              </div>
              {/* Honeypot — hidden from users and assistive tech, bots tend to fill it */}
              <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
                <label>
                  Leave this field empty
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </label>
              </div>
            </div>
            {somethingWrong ? (
              <div className="w-full text-center p-4 bg-red-700 rounded-2xl text-white">
                Something went wrong, please refresh the page and try again
              </div>
            ) : (
              <button
                onClick={() => handleSubmit()}
                disabled={submitted}
                className={`mt-16 button max-xl:mx-auto flex justify-center items-center normal-case !text-[#C3EEFD] ${showErrors ? "!bg-primary-red !text-white !border-transparent" : submitted ? "bg-primary/80" : "hover:!text-primary"}`}
              >
                {buttonLoading ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6 animate-spin"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                ) : showErrors ? (
                  "Please fill out fields above"
                ) : submitted ? (
                  "Successfully submitted"
                ) : (
                  "Submit"
                )}
              </button>
            )}
            <p className="mt-8 font-sans text-[1.5rem] leading-[2.2rem] opacity-80 max-xl:text-center">
              By submitting this form you agree to our{" "}
              <Link to="/privacy" className="text-primary-blue hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>

        <img src="/contactbrain1.webp" alt="Simple outlined structure of the human brain" className="max-xl:hidden grow" />
        </div>
      </div>
    </>
  );
}